import { Accordion, AccordionItem, Selection } from "@heroui/react";
import { Icon } from "@iconify/react";
import React from "react";
import { Link } from "react-router-dom";
import "../../pages/internal/YouWoConsumerLanding.css";
import ModernFooter from "../landing/modern/ModernFooter";
import SEOHead from "./SEOHead";
import ToolsLandingNav from "./ToolsLandingNav";
import "./ToolsLandingShell.css";
import { buildAppLink } from "../../utils/landingRedirect";

interface HowItWorksStep {
  title: string;
  description: string;
}

interface Benefit {
  title: string;
  description: string;
  icon: string;
}

interface FAQ {
  question: string;
  answer: string;
}

interface RelatedTool {
  name: string;
  description: string;
  href: string;
  icon: string;
  iconColor: string;
}

interface ToolPageLayoutProps {
  title: string;
  metaDescription: string;
  keywords?: string;
  canonicalUrl?: string;
  structuredData?: object;
  heroTitle: string;
  heroSubtitle: string;
  heroIcon: string;
  heroIconColor?: string;
  heroIconBg?: string;
  children: React.ReactNode;
  howItWorksSteps: HowItWorksStep[];
  benefitsTitle?: string;
  benefits: Benefit[];
  faqs: FAQ[];
  relatedTools: RelatedTool[];
}

const ToolPageLayout: React.FC<ToolPageLayoutProps> = ({
  title,
  metaDescription,
  keywords,
  canonicalUrl,
  structuredData,
  heroTitle,
  heroSubtitle,
  heroIcon,
  heroIconColor = "text-cornflowerblue",
  heroIconBg = "bg-blue-50",
  children,
  howItWorksSteps,
  benefitsTitle = "Why choose YouWo?",
  benefits,
  faqs,
  relatedTools,
}) => {
  const [faqSelectedKeys, setFaqSelectedKeys] = React.useState<Selection>(new Set(["0"]));

  return (
    <>
      <SEOHead
        title={title}
        description={metaDescription}
        keywords={keywords}
        canonicalUrl={canonicalUrl}
        structuredData={structuredData}
      />

      <main className="yw-consumer-page yw-tools-page yw-tools-shell" data-lang="en" data-variant="tools">
        <ToolsLandingNav />

        <section className="yw-tools-hero" id="product">
          <div className="yw-tools-hero-copy">
            <p className="yw-tools-kicker">
              <span className={`yw-tools-hero-icon ${heroIconBg}`}>
                <Icon icon={heroIcon} className={heroIconColor} />
              </span>
              Free AI tool
            </p>
            <h1>
              {heroTitle.includes("YouWo") ? heroTitle : <>{heroTitle} <span>by YouWo</span></>}
            </h1>
            <p>{heroSubtitle}</p>
            <div className="yw-tools-hero-actions">
              <a className="yw-tools-primary-link" href={buildAppLink()} target="_blank" rel="noopener noreferrer">
                Open YouWo
                <Icon icon="lucide:arrow-up-right" />
              </a>
              <Link className="yw-tools-secondary-link" to="/tools">
                Browse tools
              </Link>
            </div>
          </div>

          <div className="yw-tool-live-panel">
            {children}
          </div>
        </section>

        <section className="yw-tools-section" id="how-it-works">
          <div className="yw-tools-section-heading">
            <h2>How it works</h2>
            <p>Bring in the source, choose the output, and let YouWo turn the material into something useful.</p>
          </div>

          <div className="yw-tools-flow-grid">
            {howItWorksSteps.map((step, index) => (
              <article className="yw-tools-card" key={step.title}>
                <span className="yw-tools-card-index">{index + 1}</span>
                <h3>{step.title}</h3>
                <p>{step.description}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="yw-tools-section">
          <div className="yw-tools-section-heading">
            <h2>{benefitsTitle}</h2>
            <p>Each tool is built around the same YouWo idea: keep the source attached and make the next study step easier.</p>
          </div>

          <div className="yw-tools-feature-grid">
            {benefits.map((benefit) => (
              <article className="yw-tools-card" key={benefit.title}>
                <Icon icon={benefit.icon} className="yw-tools-card-icon" />
                <h3>{benefit.title}</h3>
                <p>{benefit.description}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="yw-tools-final-cta">
          <div>
            <h2>Use this with your real sources.</h2>
            <p>Open YouWo to save outputs, ask follow-up questions, and keep every answer connected to the material it came from.</p>
            <a href={buildAppLink()} target="_blank" rel="noopener noreferrer">
              Open YouWo
              <Icon icon="lucide:arrow-up-right" />
            </a>
          </div>
        </section>

        <section className="yw-tools-section">
          <div className="yw-tools-section-heading">
            <h2>Explore more AI tools</h2>
            <p>Use the next tool on the same source: summarize, transcribe, quiz, flashcard, or turn it into notes.</p>
          </div>

          <div className="yw-tools-related-grid">
            {relatedTools.map((tool) => (
              <Link className="yw-tools-related-card" key={tool.href} to={tool.href}>
                <Icon icon={tool.icon} className={`yw-tools-card-icon ${tool.iconColor}`} />
                <h3>{tool.name}</h3>
                <p>{tool.description}</p>
                <span>
                  Open tool
                  <Icon icon="lucide:arrow-right" />
                </span>
              </Link>
            ))}
          </div>
        </section>

        <section className="yw-tools-section" id="faq">
          <div className="yw-tools-section-heading">
            <h2>Frequently asked questions</h2>
          </div>

          <div className="yw-tools-faq-shell">
            <Accordion
              selectedKeys={faqSelectedKeys}
              onSelectionChange={setFaqSelectedKeys}
              variant="light"
              itemClasses={{
                base: "py-0 w-full",
                title: "font-normal text-medium",
                trigger: "px-2 py-0 data-[hover=true]:bg-white/60 rounded-2xl min-h-14 flex items-center",
                indicator: "text-medium",
                content: "text-small px-2",
              }}
              showDivider={false}
            >
              {faqs.map((faq, index) => {
                const isOpen = Array.from(faqSelectedKeys).includes(String(index));

                return (
                  <AccordionItem
                    key={String(index)}
                    aria-label={faq.question}
                    textValue={faq.question}
                    hideIndicator
                    title={
                      <div className="flex w-full items-center gap-4">
                        <div className={`transition-transform duration-300 ${isOpen ? "rotate-45" : "rotate-0"}`}>
                          <Icon icon="lucide:plus" className="text-xl text-gray-500" />
                        </div>
                        <span className={`text-base font-semibold md:text-lg ${isOpen ? "text-gray-900" : "text-gray-700"}`}>
                          {faq.question}
                        </span>
                      </div>
                    }
                  >
                    <div className="pl-9 pb-4 text-base leading-relaxed text-gray-600">
                      {faq.answer}
                    </div>
                  </AccordionItem>
                );
              })}
            </Accordion>
          </div>
        </section>
      </main>

      <div className="yw-tools-footer-shell">
        <ModernFooter />
      </div>
    </>
  );
};

export default ToolPageLayout;
